import { useEffect } from 'react'

import { useQuery } from '@tanstack/react-query'

import { apiGetGeneratedReferralCodeForWallet } from '@src/api/point.api'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { ReferralCode } from '@src/types/referralcode'
import { Address } from '@src/types/token'
import { createContextLogger, isEVMAddress, isFeatureEnabled } from '@src/utils'
import { useApplicationStore } from '@src/zustand_stores'

const logger = createContextLogger('useGeneratedReferralCode')

function useGeneratedReferralCodeQuery() {
    const { evmWalletAddress } = useWalletConnections()

    return useQuery<ReferralCode, Error>({
        queryKey: ['generatedReferralCode', evmWalletAddress],
        queryFn: async () => {
            // If we're on testnet, we don't support referral codes
            if (isFeatureEnabled('USE_TESTNETS_ONLY')) {
                return {
                    userAddress: '' as Address,
                    code: '',
                }
            }
            if (!evmWalletAddress || !isEVMAddress(evmWalletAddress)) {
                throw new Error('Invalid or missing EVM wallet address')
            }

            logger.info(`Fetching generated referral code for wallet ${evmWalletAddress}`)

            const data = await apiGetGeneratedReferralCodeForWallet(evmWalletAddress)
            if (!data) {
                throw new Error(`No generated referral code found for wallet ${evmWalletAddress}`)
            }

            // Return the referral code in uppercase.
            return {
                userAddress: evmWalletAddress,
                code: data.toUpperCase(),
            }
        },

        // Only run if there is a valid EVM wallet address.
        enabled: !!evmWalletAddress && isEVMAddress(evmWalletAddress),

        // Generated referral codes likely do not change.
        staleTime: Infinity,
    })
}

/**
 * Hook for fetching the generated referral code.
 * Note: We update the application store with the generated referral code.
 */
export function useFetchGeneratedReferralCode() {
    const { data, isLoading, error, refetch } = useGeneratedReferralCodeQuery()
    const setGeneratedReferralCode = useApplicationStore(state => state.setGeneratedReferralCode)

    useEffect(() => {
        logger.info('fetched generated referral code:', data)

        if (!isLoading) {
            if (data) {
                setGeneratedReferralCode(data)
            } else if (error) {
                // If wallet address is not connected or referral code
                // can't be fetched, set back to the default value.
                setGeneratedReferralCode({ userAddress: '' as Address, code: '' })
                logger.error(error)
            }
        }
    }, [data, isLoading, error, setGeneratedReferralCode])

    // We do not return the referral code directly, since it is stored in our application store.
    return { isLoading, error, refetchGeneratedReferralCode: refetch }
}
